import { useState, type Ref } from 'react'
import { useTranslation } from 'react-i18next'
import { PanelCollapseToggle, panelBodyId } from '../ui/CollapsibleSection'
import { PREVIEW_PANEL_ID } from './panel-ids'
import type { SampleMode } from './sample-data'

interface PreviewToolbarProps {
  mode: SampleMode
  onModeChange: (m: SampleMode) => void
  profileId: string
  onProfileChange: (id: string) => void
  profiles: Array<{ id: string; name?: string }>
  onRender: () => void
  onPrint?: () => void
  printEnabled?: boolean
  /** Show the sample-entity search input — only when the mounted host's
   *  SessionContext.capabilities.features includes 'sample-data'. */
  sampleEntityEnabled?: boolean
  /** Called with the trimmed code-or-name query the user submitted via the
   *  sample-entity search (issue #128 — live SKUs commonly have no code). */
  onSampleEntitySearch?: (query: string) => void
  /** Disables the sample-entity input + button while a lookup is in flight. */
  searchPending?: boolean
  /**
   * When provided, the toolbar leads with a collapse toggle for the whole
   * preview half (#189). Omitted by BlockConfiguratorRoute, whose preview has
   * no collapsed strip to expand back from.
   */
  onCollapse?: () => void
  /**
   * The Template Designer's `usePanelCollapsed().toggleRef` for the preview
   * half. This toggle is unmounted by its own collapse (the whole pane goes),
   * so it hands focus to SplitPane's strip toggle — and takes it back when
   * the strip expands the pane again.
   */
  collapseToggleRef?: Ref<HTMLButtonElement>
}

// Module scope can't call `t()` — the entry holds a KEY, translated at
// render like every other control on this toolbar (#154).
const MODES: Array<{ id: SampleMode; labelKey: string }> = [
  { id: 'sample', labelKey: 'preview.mode.sample' },
  { id: 'empty', labelKey: 'preview.mode.empty' },
  { id: 'torture', labelKey: 'preview.mode.torture' },
]

export function PreviewToolbar({
  mode,
  onModeChange,
  profileId,
  onProfileChange,
  profiles,
  onRender,
  onPrint,
  printEnabled = false,
  sampleEntityEnabled = false,
  onSampleEntitySearch,
  searchPending = false,
  onCollapse,
  collapseToggleRef,
}: PreviewToolbarProps) {
  const { t } = useTranslation()
  const [sampleEntityQuery, setSampleEntityQuery] = useState('')
  return (
    <div
      data-testid="preview-toolbar"
      className="flex items-center gap-3 border-b border-stone-200 bg-white px-4 py-2 dark:border-stone-800 dark:bg-stone-900"
    >
      {onCollapse && (
        <PanelCollapseToggle
          className="-mr-1"
          collapsed={false}
          onToggle={onCollapse}
          panelLabel={t('preview.title')}
          controls={panelBodyId(PREVIEW_PANEL_ID)}
          testId={`${PREVIEW_PANEL_ID}-toggle`}
          expandedChevron="down"
          buttonRef={collapseToggleRef}
        />
      )}
      <h3 className="text-sm font-semibold">{t('preview.title')}</h3>
      <select
        data-testid="preview-sample-select"
        aria-label={t('preview.sampleData')}
        value={mode}
        onChange={e => onModeChange(e.target.value as SampleMode)}
        className="rounded border border-stone-300 bg-white px-2 py-1 text-xs dark:border-stone-700 dark:bg-stone-950"
      >
        {MODES.map(m => (
          <option key={m.id} value={m.id}>
            {t(m.labelKey)}
          </option>
        ))}
      </select>
      <select
        data-testid="preview-profile-select"
        aria-label={t('preview.printerProfile')}
        value={profileId}
        onChange={e => onProfileChange(e.target.value)}
        className="rounded border border-stone-300 bg-white px-2 py-1 text-xs dark:border-stone-700 dark:bg-stone-950"
      >
        {profiles.map(p => (
          <option key={p.id} value={p.id}>
            {p.name ?? p.id}
          </option>
        ))}
      </select>
      {sampleEntityEnabled && (
        <form
          data-testid="preview-sample-entity-form"
          onSubmit={e => {
            e.preventDefault()
            const query = sampleEntityQuery.trim()
            if (query) onSampleEntitySearch?.(query)
          }}
          className="flex items-center gap-1"
        >
          <input
            data-testid="preview-sample-entity-input"
            type="text"
            value={sampleEntityQuery}
            onChange={e => setSampleEntityQuery(e.target.value)}
            placeholder={t('preview.sampleEntity.placeholder')}
            aria-label={t('preview.sampleEntity.label')}
            disabled={searchPending}
            className="rounded border border-stone-300 bg-white px-2 py-1 text-xs disabled:opacity-40 dark:border-stone-700 dark:bg-stone-950"
          />
          <button
            type="submit"
            data-testid="preview-sample-entity-search"
            disabled={searchPending}
            className="rounded border border-stone-300 bg-stone-50 px-2 py-1 text-xs hover:bg-stone-100 disabled:opacity-40 dark:border-stone-700 dark:bg-stone-800 dark:hover:bg-stone-700"
          >
            {t('preview.sampleEntity.search')}
          </button>
        </form>
      )}
      <button
        type="button"
        data-testid="preview-render-button"
        onClick={onRender}
        className="ml-auto rounded border border-stone-300 bg-stone-50 px-3 py-1 text-xs hover:bg-stone-100 dark:border-stone-700 dark:bg-stone-800 dark:hover:bg-stone-700"
      >
        {t('preview.render')}
      </button>
      {onPrint && (
        <button
          type="button"
          data-testid="preview-print"
          disabled={!printEnabled}
          onClick={onPrint}
          className="rounded border border-stone-300 bg-stone-50 px-3 py-1 text-xs hover:bg-stone-100 disabled:opacity-40 dark:border-stone-700 dark:bg-stone-800 dark:hover:bg-stone-700"
        >
          {t('preview.print')}
        </button>
      )}
    </div>
  )
}
